import { z } from 'zod';
import { emailSchema, passwordSchema, phoneSchema, shortTextSchema, uuidSchema } from '../primitives.js';
import { ACCOUNT_KINDS, MEMBER_ROLES, MEMBER_STATUSES } from '../enums.js';

export const registerSchema = z.object({
  fullName: shortTextSchema,
  email: emailSchema,
  password: passwordSchema,
  phone: phoneSchema.optional(),
});
export type RegisterInput = z.infer<typeof registerSchema>;

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Password is required'),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1),
  newPassword: passwordSchema,
});
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

/**
 * Deleting your own account.
 *
 * The confirmation is typed, not clicked: the word DELETE or the account's own
 * email address. A confirm button is muscle memory; a typed word is a decision.
 */
export const deleteAccountSchema = z.object({
  confirmation: z.string().trim().min(1).max(320),
});
export type DeleteAccountInput = z.infer<typeof deleteAccountSchema>;

export interface DeleteAccountResult {
  status: 'deleted';
  membershipsRemoved: number;
  invitationsRemoved: number;
  sessionsRevoked: number;
}

export const updateProfileSchema = z.object({
  fullName: shortTextSchema.optional(),
  phone: phoneSchema.nullable().optional(),
});
export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;

/** A business the authenticated user belongs to, as returned by /auth/me. */
export const membershipSummarySchema = z.object({
  businessId: uuidSchema,
  businessName: z.string(),
  businessSlug: z.string(),
  businessType: z.string(),
  currencyCode: z.string(),
  countryCode: z.string(),
  timezone: z.string(),
  role: z.enum(MEMBER_ROLES),
  status: z.enum(MEMBER_STATUSES),
  /**
   * What this member was granted or refused beyond their role.
   *
   * Without it the browser resolved permissions from the role alone, so a
   * staff member the owner had given loan rights could use them through the
   * API but never saw New loan, Issue loan or Record repayment. The server
   * still decides every request; this only stops the screen hiding what the
   * server would allow.
   */
  permissionOverrides: z
    .object({ grant: z.array(z.string()).optional(), revoke: z.array(z.string()).optional() })
    .nullable()
    .optional(),
});
export type MembershipSummary = z.infer<typeof membershipSummarySchema>;

export const authUserSchema = z.object({
  id: uuidSchema,
  email: z.string(),
  fullName: z.string(),
  phone: z.string().nullable(),
  createdAt: z.string(),
  /**
   * `personal`, `location` or `integration`. Only a personal account may
   * create a business; the client reads this to decide whether to offer it.
   * Optional so a session issued before this field existed still parses.
   */
  kind: z.enum(ACCOUNT_KINDS).optional(),
});
export type AuthUser = z.infer<typeof authUserSchema>;

/**
 * The access token is returned in the body (the client keeps it in memory);
 * the refresh token is set as an httpOnly cookie and never touches JS.
 */
export const authSessionSchema = z.object({
  accessToken: z.string(),
  expiresIn: z.number(),
  user: authUserSchema,
  memberships: z.array(membershipSummarySchema),
});
export type AuthSession = z.infer<typeof authSessionSchema>;

/**
 * Redeeming an invitation.
 *
 * `fullName` and `password` are optional here because they are only needed by
 * a recipient who has no CenoX account yet. Someone already registered simply
 * accepts, and the server decides which case they are in from the invited
 * address rather than asking the client to declare it.
 */
export const acceptInvitationSchema = z.object({
  fullName: shortTextSchema.optional(),
  password: passwordSchema.optional(),
  phone: phoneSchema.optional(),
});
export type AcceptInvitationInput = z.infer<typeof acceptInvitationSchema>;

export const invitationPreviewSchema = z.object({
  businessName: z.string(),
  email: z.string(),
  role: z.string(),
  expiresAt: z.string(),
  /** True when the invited address has no account yet, so one must be set up. */
  needsAccount: z.boolean(),
});
export type InvitationPreview = z.infer<typeof invitationPreviewSchema>;

export const forgotPasswordSchema = z.object({ email: emailSchema });
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

export const resetPasswordSchema = z.object({ password: passwordSchema });
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;
